import React, { useCallback, useEffect, useState } from "react";

interface UseDragging {
  labelRef: React.RefObject<HTMLLabelElement>;
  onDrop?: (files: FileList) => void;
}

const useDragging = ({ labelRef, onDrop }: UseDragging) => {
  const [dragging, setDragging] = useState(false);

  const dragCounter = React.useRef(0);

  const handleDragEnter = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current += 1;
    setDragging((event.dataTransfer?.items.length ?? 0) > 0);
  }, []);

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current -= 1;
    setDragging(dragCounter.current > 0);
  }, []);

  const handleDrop = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);

    onDrop?.(event.dataTransfer?.files ?? new FileList());
  }, []);

  useEffect(() => {
    const label = labelRef.current;
    if (!label) return;

    label.addEventListener("dragenter", handleDragEnter);
    label.addEventListener("dragover", handleDragOver);
    label.addEventListener("dragleave", handleDragLeave);
    label.addEventListener("drop", handleDrop);

    return () => {
      label.removeEventListener("dragenter", handleDragEnter);
      label.removeEventListener("dragover", handleDragOver);
      label.removeEventListener("dragleave", handleDragLeave);
      label.removeEventListener("drop", handleDrop);
    };
  }, [labelRef, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return dragging;
};

export default useDragging;
